import { useCallback } from 'react';
import { NoteTemplate } from '../types/notes';
import useNoteTemplates from './data/useNoteTemplates';
import { useNoteTemplateMutations } from './data/useNoteTemplateMutations';
import { MutationResult } from './data/useCategoryMutations';

export interface TemplatesModel {
  templates: NoteTemplate[];
  loading: boolean;
  addTemplate: (name: string, content: string) => Promise<MutationResult>;
  renameTemplate: (id: string, name: string) => Promise<MutationResult>;
  updateTemplate: (
    id: string,
    updates: { template_name?: string; template_content?: string },
  ) => Promise<MutationResult>;
  deleteTemplate: (id: string) => Promise<MutationResult>;
  saving: boolean;
}

/**
 * Model layer for note templates: reads the cached template list and exposes
 * create/rename/update/delete. No Supabase calls here — delegates to the data
 * hooks.
 */
export function useTemplatesModel(): TemplatesModel {
  const { templates, loading } = useNoteTemplates();
  const {
    addTemplate: addTemplateMut,
    renameTemplate,
    updateTemplate,
    deleteTemplate,
    loading: saving,
  } = useNoteTemplateMutations();

  const addTemplate = useCallback(
    (name: string, content: string) =>
      addTemplateMut({ template_name: name, template_content: content }),
    [addTemplateMut],
  );

  return {
    templates,
    loading,
    addTemplate,
    renameTemplate,
    updateTemplate,
    deleteTemplate,
    saving,
  };
}
